import { useEffect, useState } from "react";
import Base from "../../Base/views/Base";
import Box from '@mui/material/Box';
import {
    Button,
    Card,
    Grid,
    IconButton,
    TextField,
    Typography,
    Divider,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip
} from "@mui/material";
import Swal from "sweetalert2";
import axiosInstance from "../../../axiosInstance";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import HotelIcon from '@mui/icons-material/Hotel';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import Popup from "../../components/Popup";
import { downloadPDF as downloadReservationPDF } from "../../pdf/ReservationPdf";
import GuestForm from "./GuestForm";
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import BadgeIcon from '@mui/icons-material/Badge';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

const ReservationLandingPage = () => {
    const hotelID = localStorage.getItem("billing_app_hotel");
    const [data, setData] = useState([]);
    const [guests, setGuests] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [showCustomer, setShowCustomer] = useState(false);
    const [currentReservation, setCurrentReservation] = useState(null);
    console.log("currentReservation", currentReservation);
    const [hoteldata, setHotelData] = useState([]);
    const [loading, setLoading] = useState(false);

    const initialValues = {
        check_in: "",
        check_out: "",
        guest_id: "",
        room_id: "",
        hotel: hotelID,
        // Guest details fields
        guest_name: "",
        guest_email: "",
        guest_phone: "",
        guest_address: "",
        guest_aadhar_number: "",
        guest_gst_number: "",
        guest_vehicle_number: ""
    };

    const customerIntitialValues = {
        name: "",
        email: "",
        phone: "",
        payment_mode: "Cash"
    };

    /** Only validation messages — never reuse form defaults (e.g. payment_mode) or .every() always fails */
    const emptyCustomerErrors = {
        name: "",
        email: "",
        phone: "",
        total_amount: ""
    };

    const [inputData, setInputData] = useState(initialValues);
    const [customerInputData, setCustomerInputData] = useState(customerIntitialValues);
    console.log("customerInputData", customerInputData);
    const [errors, setErrors] = useState({});
    const [customerErrors, setCustomerErrors] = useState(emptyCustomerErrors);
    const [billData, setBillData] = useState({
        total_amount: 0
    });

    useEffect(() => {
        getData();
        getGuests();
        getRooms();
        getHotelData();
    }, []);

    const toBase64 = async (url) => {
        const res = await fetch(url);
        const blob = await res.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const getHotelData = async () => {
        try {
            const res = await axiosInstance.get('/api/profile/');
            const user = res?.data;
            const logoUrl = user?.hotel_detail?.logo;

            if (logoUrl) {
                const base64Logo = await toBase64(logoUrl);
                user.hotel_detail.logoBase64 = base64Logo;
            }

            setHotelData(user);
        } catch (err) {
            console.error('Failed to fetch hotel data', err);
        }
    };

    const getData = () => {
        axiosInstance(`/api/reservation/?hotel=${hotelID}`, { method: "GET" })
            .then(res => setData(res.data))
            .catch(err => console.log(err));
    };

    const getGuests = () => {
        axiosInstance(`/api/guest/?hotel=${hotelID}`, { method: "GET" })
            .then(res => setGuests(res.data))
            .catch(err => console.log(err));
    };

    const getRooms = () => {
        axiosInstance(`/api/room/?hotel=${hotelID}`, { method: "GET" })
            .then(res => setRooms(res.data))
            .catch(err => console.log(err));
    };

    const validate = (fieldValues = inputData) => {
        let temp = { ...errors };

        if ("room_id" in fieldValues)
            temp.room_id = fieldValues.room_id ? "" : "Room is required.";

        if ("check_in" in fieldValues)
            temp.check_in = fieldValues.check_in ? "" : "Check-in date is required.";

        if ("check_out" in fieldValues)
            temp.check_out = fieldValues.check_out ? "" : "Check-out date is required.";

        if ("guest_name" in fieldValues)
            temp.guest_name = fieldValues.guest_name ? "" : "Guest name is required.";

        if ("guest_phone" in fieldValues)
            temp.guest_phone = fieldValues.guest_phone ? "" : "Guest phone is required.";

        setErrors({ ...temp });
        return Object.values(temp).every(x => x === "");
    };

    const validateCustomer = (fieldValues = {}) => {
        if (!fieldValues || typeof fieldValues !== 'object') {
            fieldValues = {};
        }

        const temp = { ...emptyCustomerErrors };

        temp.name = fieldValues.name ? "" : "Name is required";

        temp.email = fieldValues.email
            ? (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(fieldValues.email)
                ? ""
                : "Enter Valid Email")
            : "";

        const phoneStr = fieldValues.phone != null ? String(fieldValues.phone).replace(/\s/g, "") : "";
        temp.phone = phoneStr
            ? /^\d{10}$/.test(phoneStr)
                ? ""
                : "Enter a valid 10-digit mobile number"
            : "";

        const amount = fieldValues.total_amount ?? billData?.total_amount;
        temp.total_amount =
            amount != null && Number(amount) > 0 ? "" : "Please enter a valid amount";

        setCustomerErrors(temp);
        return Object.values(temp).every((x) => x === "");
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputData(prev => ({
            ...prev,
            [name]: value
        }));

        // If selecting a guest from dropdown, auto-fill their details
        if (name === 'guest_id' && value) {
            const selectedGuest = guests.find(g => g.id === value);
            if (selectedGuest) {
                setInputData(prev => ({
                    ...prev,
                    guest_id: value,
                    guest_name: selectedGuest.name,
                    guest_email: selectedGuest.email,
                    guest_phone: selectedGuest.phone,
                    guest_address: selectedGuest.address,
                    guest_aadhar_number: selectedGuest.aadhar_number,
                    guest_gst_number: selectedGuest.gst_number,
                    guest_vehicle_number: selectedGuest.vehicle_number
                }));
            }
        }
    };

    const handleCustomerChange = (e) => {
        setCustomerInputData({ ...customerInputData, [e.target.name]: e.target.value });
    };

    const handleClose = () => {
        setOpen(false);
        setInputData(initialValues);
        setErrors({});
        setSubmitted(false);
    };

    const handleCustomerClose = () => {
        setCustomerInputData(customerIntitialValues);
        setCustomerErrors(emptyCustomerErrors);
        setBillData({ total_amount: 0 });
        setShowCustomer(false);
    };


    const handleSubmit = async () => {
        setSubmitted(true);

        if (!validate()) {
            if (submitted) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Please fill all required fields correctly',
                });
            }
            return;
        }

        setIsSubmitting(true);

        try {
            const api = inputData?.id
                ? `/api/reservation/${inputData.id}/`
                : `/api/reservation/`;
            const method = inputData?.id ? "PUT" : "POST";

            const payload = {
                check_in: inputData.check_in + ":00Z",
                check_out: inputData.check_out + ":00Z",
                room_id: inputData.room_id,
                hotel: hotelID,
                guest: {
                    name: inputData.guest_name,
                    email: inputData.guest_email,
                    phone: inputData.guest_phone,
                    address: inputData.guest_address,
                    aadhar_number: inputData.guest_aadhar_number,
                    gst_number: inputData.guest_gst_number,
                    vehicle_number: inputData.guest_vehicle_number,
                    hotel: hotelID,
                },
            };

            const response = await axiosInstance(api, {
                method: method,
                data: payload,
            });

            getData();
            handleClose();

            await Swal.fire({
                icon: 'success',
                title: inputData?.id ? 'Reservation updated!' : 'Reservation added!',
                timer: 1500,
                showConfirmButton: false,
            });

        } catch (err) {
            handleClose();
            console.error("Reservation error:", err);

            let errorMessage = 'Failed to process reservation';

            if (err.response?.data) {
                if (Array.isArray(err.response.data)) {
                    errorMessage = err.response.data[0]; // Django ValidationError
                } else if (err.response.data.detail) {
                    errorMessage = err.response.data.detail;
                } else if (err.response.data.non_field_errors) {
                    errorMessage = err.response.data.non_field_errors.join('\n');
                } else {
                    errorMessage = JSON.stringify(err.response.data);
                }
            }

            await Swal.close();

            await Swal.fire({
                icon: 'error',
                title: 'Reservation Conflict',
                html: `
                    <div style="text-align: center;">
                        <p style="font-size: 1.2rem; margin-bottom: 1rem;">
                            ${errorMessage}
                        </p>
                        <p style="color: #666; font-size: 0.9rem;">
                            Please choose different dates or another room.
                        </p>
                    </div>
                `,
                customClass: {
                    popup: 'swal-popup-custom',
                    container: 'swal-overlay-zfix'
                },
                showConfirmButton: true,
                confirmButtonText: 'OK',
                allowOutsideClick: false,
                backdrop: 'rgba(0,0,0,0.7)'
            });

        } finally {
            setIsSubmitting(false);
        }
    };



    const handleClick = (res) => {
        setOpen(true);
        setInputData({
            id: res.id,
            check_in: res.check_in ? res.check_in.slice(0, 16) : "",
            check_out: res.check_out ? res.check_out.slice(0, 16) : "",
            guest_id: res.guest?.id || "",
            room_id: res.room?.id || "",
            hotel: hotelID,
            // Guest details
            guest_name: res.guest?.name || "",
            guest_email: res.guest?.email || "",
            guest_phone: res.guest?.phone || "",
            guest_address: res.guest?.address || "",
            guest_aadhar_number: res.guest?.aadhar_number || "",
            guest_gst_number: res.guest?.gst_number || "",
            guest_vehicle_number: res.guest?.vehicle_number || ""
        });
    };

    const handlePrint = (e, reservation) => {
        e?.stopPropagation();
        setCurrentReservation(reservation);

        // Calculate initial total amount based on room price and duration
        const room = rooms.find(r => r.id === reservation.room?.id);
        const checkIn = new Date(reservation.check_in);
        const checkOut = new Date(reservation.check_out);
        const duration = (checkOut - checkIn) / (1000 * 60 * 60 * 24);
        const calculatedAmount = room ? room.price * duration : 0;

        // Set initial values
        setBillData({
            total_amount: calculatedAmount
        });

        // Autofill customer details if available
        if (reservation.guest) {
            setCustomerInputData({
                name: reservation.guest.name || "",
                email: reservation.guest.email || "",
                phone: reservation.guest.phone || ""
            });
        }

        setShowCustomer(true);
    };

    const handleCustomerSubmit = async () => {
        if (!validateCustomer(customerInputData)) {
            await Swal.fire({
                icon: "error",
                title: "Check the form",
                text: "Please fix the highlighted fields before generating the bill.",
            });
            return;
        }

        if (!currentReservation) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No reservation selected',
            });
            return;
        }

        setLoading(true);

        try {
            const billPayload = {
                total_amount: billData.total_amount || 0,
                customer_name: customerInputData?.name,
                customer_phone: customerInputData?.phone,
                customer_email: customerInputData?.email,
                payment_mode: customerInputData?.payment_mode || "Cash",
                reservation: currentReservation?.id
            };

            const response = await axiosInstance("/api/bill/", {
                method: "POST",
                data: billPayload
            });

            const calculatedBillData = response.data;

            await downloadReservationPDF(calculatedBillData, currentReservation, hoteldata);

            Swal.fire({
                icon: 'success',
                title: 'Bill generated successfully!',
                timer: 1500
            });

            handleCustomerClose();
        } catch (err) {
            let errorMessage = 'Failed to generate bill';
            const data = err?.response?.data ?? err;
            if (data) {
                if (typeof data === 'string') {
                    errorMessage = data;
                } else if (data.detail) {
                    errorMessage = data.detail;
                } else if (data.non_field_errors) {
                    errorMessage = Array.isArray(data.non_field_errors)
                        ? data.non_field_errors.join(', ')
                        : data.non_field_errors;
                } else if (typeof data === 'object') {
                    errorMessage = JSON.stringify(data);
                }
            }
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };


    const handleDelete = (e, id) => {
        e?.stopPropagation();
        Swal.fire({
            title: "Are you sure?",
            text: "This reservation will be deleted.",
            showCancelButton: true,
            confirmButtonText: "Delete",
            icon: 'warning',
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                axiosInstance(`/api/reservation/${id}/`, { method: "DELETE" })
                    .then(res => {
                        getData();
                        Swal.fire({
                            icon: 'success',
                            title: 'Deleted!',
                            timer: 1500
                        });
                    })
                    .catch(err => console.log(err));
            }
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);

        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'UTC'
        };

        return date.toLocaleDateString('en-US', options);
    };

    return (
        <Base>
            {/* Reservation Form Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" >
                <DialogTitle>{inputData?.id ? "Edit Reservation" : "Add New Reservation"}</DialogTitle>
                <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            label="Full Name "
                            fullWidth
                            required
                            name="guest_name"
                            onChange={handleChange}
                            value={inputData?.guest_name || ""}
                            size="small"
                            sx={{ mb: 2 }}
                            error={!!errors.guest_name}
                            helperText={errors.guest_name}
                            InputProps={{
                                startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                            }}
                        />

                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                label="Email"
                                fullWidth
                                name="guest_email"
                                onChange={handleChange}
                                value={inputData?.guest_email || ""}
                                size="small"
                                InputProps={{
                                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                            />
                            <TextField
                                label="Phone *"
                                fullWidth
                                name="guest_phone"
                                onChange={handleChange}
                                value={inputData?.guest_phone || ""}
                                size="small"
                                error={!!errors.guest_phone}
                                helperText={errors.guest_phone}
                                InputProps={{
                                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                            />
                        </Box>

                        <TextField
                            label="Address"
                            fullWidth
                            name="guest_address"
                            onChange={handleChange}
                            value={inputData?.guest_address || ""}
                            size="small"
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: <HomeIcon sx={{ mr: 1, color: 'action.active' }} />
                            }}
                        />

                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                label="Aadhaar Number"
                                fullWidth
                                name="guest_aadhar_number"
                                onChange={handleChange}
                                value={inputData?.guest_aadhar_number || ""}
                                size="small"
                                InputProps={{
                                    startAdornment: <BadgeIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                            />
                            <TextField
                                label="GST Number"
                                fullWidth
                                name="guest_gst_number"
                                onChange={handleChange}
                                value={inputData?.guest_gst_number || ""}
                                size="small"
                            />
                        </Box>

                        <TextField
                            label="Vehicle Number"
                            fullWidth
                            name="guest_vehicle_number"
                            onChange={handleChange}
                            value={inputData?.guest_vehicle_number || ""}
                            size="small"
                            InputProps={{
                                startAdornment: <DirectionsCarIcon sx={{ mr: 1, color: 'action.active' }} />
                            }}
                        />

                        <FormControl fullWidth size="small" sx={{ mb: 2, mt: 2 }} error={!!errors.room_id}>
                            <InputLabel>Room *</InputLabel>
                            <Select
                                name="room_id"
                                value={inputData?.room_id || ""}
                                onChange={handleChange}
                                label="Room *"
                                required
                            >
                                {rooms?.map(room => (
                                    <MenuItem key={room.id} value={room.id}>
                                        {room?.no} - {room?.name}
                                    </MenuItem>
                                ))
                                }
                            </Select>
                            {errors.room_id && <Typography color="error" variant="caption">{errors.room_id}</Typography>}
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                label="Check-in *"
                                type="datetime-local"
                                fullWidth
                                name="check_in"
                                onChange={handleChange}
                                value={inputData?.check_in ? inputData.check_in.slice(0, 16) : ""}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.check_in}
                                helperText={errors.check_in}
                                InputProps={{
                                    startAdornment: <EventIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                            />
                            <TextField
                                label="Check-out *"
                                type="datetime-local"
                                fullWidth
                                name="check_out"
                                onChange={handleChange}
                                value={inputData?.check_out ? inputData.check_out.slice(0, 16) : ""}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.check_out}
                                helperText={errors.check_out}
                                InputProps={{
                                    startAdornment: <EventIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="contained" color="error">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Customer Form Popup for Bill Generation */}
            <Popup
                open={showCustomer}
                handleClose={handleCustomerClose}
                title={"Customer Information & Billing"}
                handleSubmit={handleCustomerSubmit}
                maxWidth="sm"
                saveText="Generate Bill"
                loading={loading}
            >
                <GuestForm
                    handleChange={handleCustomerChange}
                    inputData={customerInputData}
                    errors={customerErrors}
                    billData={billData}
                    setBillData={setBillData}
                />
            </Popup>

            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2 }}>
                <Button 
                    variant="contained" 
                    onClick={() => setOpen(true)}
                    size="small"
                    sx={{ 
                        borderRadius: 1.5,
                        px: 2,
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(139, 68, 255, 0.3)',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(139, 68, 255, 0.4)',
                            transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    Add Reservation
                </Button>
            </Box>

            {/* Reservations List */}
            <Grid container spacing={2}>
                {data.map((res) => (
                    <Grid item xs={12} sm={6} md={4} key={res.id}>
                        <Card sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography fontWeight="600">
                                    {res.guest?.name || "No guest assigned"}
                                </Typography>
                                <Box>
                                    <Tooltip title="Generate Bill">
                                        <IconButton size="small" onClick={(e) => handlePrint(e, res)}>
                                            <LocalPrintshopIcon fontSize="small" color="primary" />
                                        </IconButton>
                                    </Tooltip>
                                    <IconButton size="small" onClick={() => handleClick(res)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={(e) => handleDelete(e, res.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 1 }} />

                            <Stack spacing={1}>
                                {res.room?.name && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Room:</Typography>
                                        <Chip
                                            label={`${res.room.name} (${res.room.type})`}
                                            size="small"
                                            icon={<HotelIcon fontSize="small" />}
                                        />
                                    </Box>
                                )}

                                {res.guest?.phone && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">Guest Phone:</Typography>
                                        <Typography>{res.guest.phone}</Typography>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Check-in:</Typography>
                                    <Typography>{formatDate(res.check_in)}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Check-out:</Typography>
                                    <Typography>{formatDate(res.check_out)}</Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Empty State */}
            {data.length === 0 && (
                <Box sx={{ textAlign: "center", p: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Reservations Found
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<EventIcon />}
                        onClick={() => setOpen(true)}
                    >
                        Add Reservation
                    </Button>
                </Box>
            )}
        </Base>
    );
};

export default ReservationLandingPage;